import React, { useEffect, useState } from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi, useDashboard } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { DateTimeRange } from '@/insights/components/DateTimeRange'
import type { Asset, InsightParametersType } from '@/types/dashboard-types'

const dateOptions: DropdownOption[] = [
  { label: 'Insight Default', value: '' },
  ...DateOptions.filter((o) => o.value !== ''),
]

function Permissions({ onClose }: { onClose?: () => void }) {
  const dashboardContext = useDashboard()
  const { companies } = useCompany()
  const { api } = useApi()
  const dashboard = dashboardContext.currentDashboard
  const params = dashboard?.parameters || {}

  const [companyId, setCompanyId] = useState<number | undefined>(
    params.companyId ? Number(params.companyId) : undefined
  )
  const [assetId, setAssetId] = useState<string | undefined>(params.assetId ? String(params.assetId) : undefined)
  const [date, setDate] = useState<string>((params.date as string) || '')
  const [from, setFrom] = useState<string>((params.from as string) || '')
  const [to, setTo] = useState<string>((params.to as string) || '')
  const [showInNav, setShowInNav] = useState<boolean>(!!params.showInNav)
  const [_assetOptions, setAssetOptions] = useState<DropdownOption[]>([])
  const [_assetPlaceholder, setAssetPlaceholder] = useState('Select a company first...')

  // Load assets when company changes
  useEffect(() => {
    if (!api) {
      return
    }
    if (!companyId) {
      setAssetOptions([])
      setAssetPlaceholder('Select a company first...')
      return
    }
    const fn = async () => {
      setAssetPlaceholder('Loading...')
      const response = await api.get<Asset[]>({
        path: `/assets?companyId=${companyId}&skip=0&take=10000&deleted=false`,
        origin: 'DashboardConfig',
      })
      if (response.error || !response.data) {
        setAssetPlaceholder('Error loading assets.')
        setAssetOptions([])
      } else {
        setAssetPlaceholder('Select an asset...')
        setAssetOptions(
          response.data.map((a) => {
            const type = []
            if (a.fridgeType?.make && !/unknown/i.test(a.fridgeType.make)) type.push(a.fridgeType.make)
            if (a.fridgeType?.model && !/unknown/i.test(a.fridgeType.model)) type.push(a.fridgeType.model)
            const mm = type.length ? `: ${type.join('/')}` : ''
            return { label: `${a.name}${mm}`, value: String(a.id) }
          })
        )
      }
    }
    fn()
  }, [companyId, api])

  const companyOptions: DropdownOption[] = [
    { label: 'Insight Default', value: '' },
    ...companies.map((c) => ({ label: c.name, value: String(c.id) })),
  ]

  const handleCompanyChange = (opt: DropdownOption | null) => {
    const newId = opt?.value ? Number(opt.value) : undefined
    setCompanyId(newId)
    setAssetId(undefined) // clear asset when company changes
  }

  const handleSave = () => {
    const parameters: InsightParametersType = {}
    if (companyId) parameters.companyId = companyId
    if (assetId) parameters.assetId = assetId
    if (date) parameters.date = date
    if (date === 'custom') {
      if (from) parameters.from = from
      if (to) parameters.to = to
    }
    const allDefaults = !companyId && !date
    parameters.showInNav = allDefaults ? false : showInNav
    dashboardContext.updateDashboardParameters(parameters)
    onClose?.()
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      <h3 className="text-lg font-semibold">Dashboard-Wide Parameters</h3>
      <p className="text-sm text-base-content/60">
        Set parameters that apply to all insights on this dashboard. Choose "Insight Default" to let each insight use
        its own setting.
      </p>

      {/* Company */}
      <div>
        <label className="block text-base-content mb-2">Company</label>
        <Dropdown
          options={companyOptions}
          handleSingleSelectItem={handleCompanyChange}
          selectedItem={companyOptions.find((o) => Number(o.value) === companyId) || companyOptions[0]}
          placeholder="Select company..."
        />
      </div>

      {/* Asset — hidden for now, uncomment to re-enable
      <div>
        <label className="block text-base-content mb-2">Asset</label>
        <Dropdown
          options={[{ label: 'Insight Default', value: '' }, ...assetOptions]}
          handleSingleSelectItem={(opt: DropdownOption | null) => setAssetId(opt?.value || undefined)}
          selectedItem={assetOptions.find((o) => o.value === assetId) || { label: 'Insight Default', value: '' }}
          placeholder={companyId ? assetPlaceholder : 'Select a company first...'}
          animateDots={/^Loading/.test(assetPlaceholder)}
        />
      </div>
      */}

      {/* Date Range */}
      <div>
        <label className="block text-base-content mb-2">Date Range</label>
        <Dropdown
          options={dateOptions}
          handleSingleSelectItem={(opt: DropdownOption | null) => {
            setDate(opt?.value || '')
            if (opt?.value !== 'custom') {
              setFrom('')
              setTo('')
            }
          }}
          selectedItem={dateOptions.find((o) => o.value === date) || dateOptions[0]}
          placeholder="Select date range..."
        />
      </div>

      {/* Custom date pickers */}
      {date === 'custom' && (
        <DateTimeRange
          from={from}
          to={to}
          onChange={({ from: f, to: t }) => {
            setFrom(f || '')
            setTo(t || '')
          }}
          help={<span />}
        />
      )}

      {/* Show in Nav checkbox — disabled when all options are at defaults */}
      {(() => {
        const allDefaults = !companyId && !date
        return (
          <label
            className={`flex items-center gap-3 ${allDefaults ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              checked={allDefaults ? false : showInNav}
              onChange={(e) => setShowInNav(e.target.checked)}
              disabled={allDefaults}
            />
            <span className="text-sm">Enable changing in navigation bar</span>
          </label>
        )
      })()}

      {/* Save button */}
      <button onClick={handleSave} className="btn btn-primary w-full">
        Save Dashboard Parameters
      </button>
    </div>
  )
}

export default Permissions
